import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useData } from "../../contexts/DataContext";
import "./DashboardCard.css";
import "./UpcomingAssignments.css"; // TODO migrate to tokens
import '../primitives/FilterDropdown.css';
import '../primitives/Button.css';
import '../primitives/Stack.css';
import '../primitives/Card.css';
import { AssignmentApi } from "../../services/api/AssignmentApi";
import AssignmentCompletionModal from './AssignmentCompletionModal';
import useIsMobile from '../../hooks/useIsMobile';
import AddNewButton from '../common/AddNewButton';

function UpcomingAssignments({ courseEndeavorId, courseName, eventId, defaultStatus = 'incomplete', defaultDue, onCountUpdate }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getFilteredEndeavors, getFilteredAssignments, getFilteredEvents, isLoading, clearCache, invalidateUserAssignmentsCache } = useData();
  const isMobile = useIsMobile();

  const [assignments, setAssignments] = useState([]);
  const [endeavors, setEndeavors] = useState([]);
  const [error, setError] = useState(null);
  // Init filters (endeavor locked if courseEndeavorId)
  const [filters, setFilters] = useState(() => ({
    status: defaultStatus,              // allow caller to override default
    due: defaultDue ?? (eventId ? 'all' : 'upcoming'),                    // now configurable (EventView sets to 'all')
    endeavor: courseEndeavorId ? String(courseEndeavorId) : 'all'
  }));
  const [completingId, setCompletingId] = useState(null);
  const [completionModal, setCompletionModal] = useState({
    open: false,
    action: null,          // 'complete' | 'incomplete'
    assignment: null,
    targetCompleted: null, // boolean
    loading: false
  });
  // cache events by id so assignment due date can use event start time
  const [eventsById, setEventsById] = useState({});
  // Track whether we've loaded aggregated events (used for event-linked due dates)
  const aggregatedEventsLoadedRef = React.useRef(false);

  const userId = user?.id || user?.userId;
  const endeavorsLoading = isLoading(`endeavors_user-${userId}`);

  // Add near top of component (after state declarations):
  const onCountUpdateRef = React.useRef(onCountUpdate);
  React.useEffect(()=>{ onCountUpdateRef.current = onCountUpdate; },[onCountUpdate]);
  const lastCountRef = React.useRef(-1);
  const emitCountIfChanged = React.useCallback((count)=>{
    if(onCountUpdateRef.current && lastCountRef.current !== count){
      lastCountRef.current = count;
      onCountUpdateRef.current(count);
    }
  },[]);

  // Stabilize DataContext method references to prevent callback recreation
  const getFilteredEndeavorsRef = useRef(getFilteredEndeavors);
  const getFilteredAssignmentsRef = useRef(getFilteredAssignments);
  const getFilteredEventsRef = useRef(getFilteredEvents);
  useEffect(() => { 
    getFilteredEndeavorsRef.current = getFilteredEndeavors;
    getFilteredAssignmentsRef.current = getFilteredAssignments;
    getFilteredEventsRef.current = getFilteredEvents;
  }, [getFilteredEndeavors, getFilteredAssignments, getFilteredEvents]);  // Load endeavors using DataContext
  const loadEndeavors = useCallback(async () => {
    if (!userId) return [];
    if (courseEndeavorId) {
      const single = [{
        id: Number(courseEndeavorId),
        endeavorId: Number(courseEndeavorId),
        name: courseName || 'Course'
      }];
      setEndeavors(single);
      return single;
    }
    try {
      setError(null);
      console.log('UpcomingAssignments: Loading endeavors for user:', userId);
      
      const endeavorsList = await getFilteredEndeavorsRef.current(userId);
      setEndeavors(endeavorsList);
      
      return endeavorsList;
    } catch (err) {
      console.error('UpcomingAssignments: Error loading endeavors:', err);
      setError(err);
      return [];
    }
  }, [userId, courseEndeavorId, courseName]);

  // Load assignments for all endeavors using DataContext
  // ✅ Use DataContext filtering to get assignments from cache
  const loadUserAssignments = useCallback(async (endeavorsList) => {
    try {
      setError(null);
      
      // Use DataContext filtering - it handles cache lookup and filtering
      const filterParams = {};
      if (courseEndeavorId) filterParams.endeavorId = courseEndeavorId;
      if (eventId) filterParams.eventId = eventId;

      const list = await getFilteredAssignmentsRef.current(filterParams);
      
      // Enrich with endeavor names
      const nameById = new Map(endeavorsList.map(e => [Number(e.endeavorId||e.id), e.name]));
      const enriched = list.map(a => {
        const eid = a.endeavorId || a.endeavorID || a.endeavor_id;
        return {
          ...a,
          endeavorId: eid,
          endeavorName: nameById.get(Number(eid)) || a.endeavorName || courseName || 'Course'
        };
      });
      
      setAssignments(enriched);
      emitCountIfChanged(enriched.length);
    } catch (error) {
      console.error('UpcomingAssignments: Error loading filtered assignments:', error);
      setError(error);
    }
  }, [courseEndeavorId, eventId, courseName, emitCountIfChanged]);

  // Load data when component mounts or user changes
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    loadEndeavors().then(list => { if(!cancelled) return loadUserAssignments(list); });
    return () => { cancelled = true; };
  }, [userId, loadEndeavors, loadUserAssignments]);

  const handleRefresh = useCallback(async () => {
    const endeavorsList = await loadEndeavors();
    await loadUserAssignments(endeavorsList);
  }, [loadEndeavors, loadUserAssignments]);

  const handleToggleCompletion = useCallback((assignment, makeCompleted) => {
    // Open confirmation modal BEFORE any state mutation
    setCompletionModal({
      open: true,
      action: makeCompleted ? 'complete' : 'incomplete',
      assignment,
      targetCompleted: makeCompleted,
      loading: false
    });
  }, []);

  const confirmCompletionChange = useCallback(async () => {
    setCompletionModal(m => ({ ...m, loading: true }));
    const { assignment, targetCompleted } = completionModal;
    if (!assignment) return;

    const id = assignment.assignmentId || assignment.id;
    const endeavorId = assignment.endeavorId ?? assignment.endeavorID ?? assignment.endeavor_id;

    // Optimistic update AFTER user confirmed
    setAssignments(prev => prev.map(a => {
      const aid = a.assignmentId || a.id;
      if (aid !== id) return a;
      const completed = targetCompleted;
      return {
        ...a,
        completed,
        status: completed ? 'Completed' : 'Pending',
        submissionStatus: completed ? 'Completed' : 'Pending',
        completedDatetime: completed ? new Date().toISOString() : null
      };
    }));

    try {
      setCompletingId(id);
      if (targetCompleted) {
        const res = await AssignmentApi.markAssignmentCompleted(id);
        if (!res.ok) throw new Error((await res.text().catch(()=>'')) || 'Failed to mark complete');
      } else {
        const payload = {
          assignmentId: id,
          endeavorId,
          eventId: assignment.eventId ?? null,
          title: assignment.title || assignment.name,
          description: assignment.description ?? null,
          assignedDate: assignment.assignedDate ?? null,
          dueDatetime: assignment.dueDatetime ?? assignment.dueDate ?? null,
          completed: false,
          completedDatetime: null,
          createdAt: assignment.createdAt ?? null,
          updatedAt: assignment.updatedAt ?? null
        };
        const res = await AssignmentApi.updateAssignment(id, payload);
        if (!res.ok) throw new Error((await res.text().catch(()=>'')) || 'Failed to mark incomplete');
      }

  // Invalidate specific assignment cache + any aggregated user assignments so next fetch is fresh
  clearCache(`assignment_${id}`);
  invalidateUserAssignmentsCache();
  // Re-run aggregated fetch to sync state with server
  handleRefresh();

      setCompletionModal({
        open: false,
        action: null,
        assignment: null,
        targetCompleted: null,
        loading: false
      });
      // Notify other dashboard modules (e.g., ScoutIQ progress) to recompute
      // TODO: Consider lifting assignments state or using a shared context instead of a DOM event.
      window.dispatchEvent(new Event('assignments:updated'));
    } catch (err) {
      alert(err.message || 'Failed to update assignment status.');
      // Revert optimistic update
      setAssignments(prev => prev.map(a => {
        const aid = a.assignmentId || a.id;
        if (aid !== id) return a;
        return {
          ...a,
          completed: !completionModal.targetCompleted,
          status: !completionModal.targetCompleted ? 'Completed' : 'Pending',
          submissionStatus: !completionModal.targetCompleted ? 'Completed' : 'Pending'
        };
      }));
      setCompletionModal(m => ({ ...m, loading: false }));
    } finally {
      setCompletingId(null);
    }
  }, [completionModal, clearCache, invalidateUserAssignmentsCache, handleRefresh]);

  const handleAssignmentClick = useCallback((assignment) => {
    const assignmentId = assignment.assignmentId || assignment.id;
    if (assignmentId) {
      navigate(isMobile ? `/m/assignment/${assignmentId}` : `/assignment/${assignmentId}`);
    }
  }, [navigate, isMobile]);

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  // Helper: get effective due date (event start > assignment due)
  const getEffectiveDueDate = React.useCallback((a) => {
    const ev = a?.eventId ? (eventsById[a.eventId] || null) : null;
    const raw =
      (ev && (ev.startDatetime || ev.eventDate)) ||
      a?.dueDatetime ||
      a?.dueDate ||
      null;
    if (!raw) return null;
    const d = typeof raw === 'string'
      ? (raw.includes('T') ? new Date(raw) : new Date(`${raw}T00:00:00`))
      : new Date(raw);
    return isNaN(d) ? null : d;
  }, [eventsById]);

  // Filter assignments based on current UI filters (status/due/endeavor dropdowns)
  const getDisplayedAssignments = () => {
    // Scope to event if eventId provided
    let filteredAssignments = eventId
      ? assignments.filter(a => Number(a.eventId || a.eventID) === Number(eventId))
      : [...assignments];

    if (filters.endeavor !== 'all') {
      filteredAssignments = filteredAssignments.filter(assignment => 
        (assignment.endeavorId) === parseInt(filters.endeavor)
      );
    }

    const isAssignmentCompleted = (assignment) =>
      assignment.completed ||
      assignment.status === 'Completed' ||
      assignment.submissionStatus === 'Completed';

    if (filters.status === 'incomplete') {
      filteredAssignments = filteredAssignments.filter(a => !isAssignmentCompleted(a));
    } else if (filters.status === 'complete') {
      filteredAssignments = filteredAssignments.filter(a => isAssignmentCompleted(a));
    }

    const now = new Date();
    if (filters.due === 'upcoming') {
      filteredAssignments = filteredAssignments.filter(a => {
        const dueDate = getEffectiveDueDate(a);
        return dueDate && dueDate >= now;
      });
    } else if (filters.due === 'overdue') {
      filteredAssignments = filteredAssignments.filter(a => {
        const dueDate = getEffectiveDueDate(a);
        return dueDate && dueDate < now && !a.completed;
      });
    }

    // Sorting:
    // Always group incomplete assignments above complete assignments.
    // Within each group:
    //   Complete -> reverse chronological (latest due date first)
    //   Incomplete / All -> chronological (earliest due date first)
    filteredAssignments.sort((a, b) => {
      const aCompleted = a.completed || a.status === 'Completed' || a.submissionStatus === 'Completed';
      const bCompleted = b.completed || b.status === 'Completed' || b.submissionStatus === 'Completed';
      // Incomplete first, complete last
      if (aCompleted !== bCompleted) return aCompleted ? 1 : -1;

      const da = getEffectiveDueDate(a)?.getTime() ?? 0;
      const db = getEffectiveDueDate(b)?.getTime() ?? 0;
      if (aCompleted && bCompleted) {
        if (db !== da) return db - da; // descending for completed
      } else {
        if (da !== db) return da - db; // ascending for incomplete
      }
      // Tie-breaker: title/name
      const ta = (a.name || a.title || '').localeCompare(b.name || b.title || '');
      if (ta !== 0) return ta;
      // Final tie-breaker: id
      return (a.assignmentId || a.id || 0) - (b.assignmentId || b.id || 0);
    });

    return filteredAssignments;
  };

  // Aggregated events hydration (single call) to enrich assignments referencing eventId
  useEffect(() => {
    if (!assignments?.length || aggregatedEventsLoadedRef.current) return;
    const anyEventLinked = assignments.some(a => a.eventId || a.eventID);
    if (!anyEventLinked) return;
    (async () => {
      try {
        const now = new Date();
        const anchor = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const past = new Date(anchor.getTime() - 60 * 24 * 60 * 60 * 1000);
        const future = new Date(anchor.getTime() + 365 * 24 * 60 * 60 * 1000);
        const params = {
          startDate: past.toISOString(),
          endDate: future.toISOString()
        };
        const list = await getFilteredEventsRef.current(params);
        const byId = {};
        list.forEach(ev => {
          const id = ev.eventId || ev.id;
          if (id != null) byId[id] = ev;
        });
        setEventsById(byId);
        aggregatedEventsLoadedRef.current = true;
      } catch {
        // ignore
      }
    })();
  }, [assignments]);

  // Prefer the linked event's start time over the assignment's own due date
  const formatDueDateForAssignment = (assignment) => {
    const ev = assignment?.eventId ? (eventsById[assignment.eventId] || null) : null;
    const raw =
      (ev && (ev.startDatetime || ev.eventDate)) ||
      assignment?.dueDatetime ||
      assignment?.dueDate ||
      null;
    if (!raw) return 'No due date';
    const dueDate = raw.includes?.('T') ? new Date(raw) : new Date(`${raw}T00:00:00`);
     if (isNaN(dueDate.getTime())) return 'Invalid date';
     
     const now = new Date();
     const tomorrow = new Date(now);
     tomorrow.setDate(now.getDate() + 1);
     
     const isToday = dueDate.toDateString() === now.toDateString();
     const isTomorrow = dueDate.toDateString() === tomorrow.toDateString();
     
     if (isToday) {
       return `${dueDate.toLocaleDateString('en-US', { 
         weekday: 'long' 
       })} at ${dueDate.toLocaleTimeString('en-US', { 
         hour: 'numeric', 
         minute: '2-digit',
         hour12: true 
       })}`;
     } else if (isTomorrow) {
       return `${dueDate.toLocaleDateString('en-US', { 
         weekday: 'long' 
       })} at ${dueDate.toLocaleTimeString('en-US', { 
         hour: 'numeric', 
         minute: '2-digit',
         hour12: true 
       })}`;
     } else {
       return dueDate.toLocaleDateString('en-US', { 
         weekday: 'long',
         month: 'short', 
         day: 'numeric',
         hour: 'numeric',
         minute: '2-digit',
         hour12: true
       });
     }
   };

  // ✅ NEW: Calculate completed and remaining assignments
  const getAssignmentCounts = () => {
    const base = eventId
      ? assignments.filter(a => Number(a.eventId || a.eventID) === Number(eventId))
      : assignments;
    const completedCount = base.filter(a => 
       a.completed || a.status === 'Completed' || a.submissionStatus === 'Completed'
     ).length;
    const totalCount = base.length;
    const remainingCount = totalCount - completedCount;
    return { completedCount, remainingCount, totalCount };
  };

  const filteredAssignments = getDisplayedAssignments();
  const { completedCount, remainingCount } = getAssignmentCounts();
  const loading = endeavorsLoading;

  // ADD: dynamic title (event overrides course)
  const assignmentsTitle = eventId
    ? 'Event Assignments'
    : (courseEndeavorId ? 'Course Assignments' : 'Assignments');

  // Remove existing effect:
  // useEffect(()=>{ if(onCountUpdate) onCountUpdate(assignments.length); },[assignments, onCountUpdate]);

  // Add new watcher:
  React.useEffect(()=>{
    if(!endeavorsLoading){
      emitCountIfChanged(assignments.length);
    }
  },[assignments, endeavorsLoading, emitCountIfChanged]);

  return (
    <div className={`dashboard-card upcoming-assignments-card ${isMobile ? 'full-height' : ''}`}>
      <div className="card-main-header">
        <div className="card-title-with-counts">
          <h2 className="card-main-title">
            <span className="header-icon-wrapper assignments-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 11l3 3L22 4"/>
                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
              </svg>
            </span>
            {assignmentsTitle}
          </h2>
          <div className="card-counts">
            {loading ? (
              <span className="count-text">Loading...</span>
            ) : (
              <span className="count-text">
                <span className="count-number">{completedCount}</span> completed, <span className="count-number">{remainingCount}</span> remaining
              </span>
            )}
          </div>
        </div>
        <div className="card-header-actions">
            <AddNewButton
              onClick={() => navigate(
                isMobile ? '/m/create-assignment' : '/create-assignment',
                {
                  state: {
                    prefill: {
                      endeavorId: courseEndeavorId || null,
                      eventId: eventId || null,
                      courseName
                    },
                    returnTo: window.location.pathname
                  }
                }
              )}
              disabled={loading}
              ariaLabel="Create new assignment"
            />
        </div>
      </div>

      <div className="card-filters-segmented">
        <div className="segmented-control" role="radiogroup" aria-label="Status filter">
          {[
            { value: 'incomplete', label: 'Incomplete' },
            { value: 'complete', label: 'Complete' },
            { value: 'all', label: 'All' }
          ].map(opt => (
            <button
              key={opt.value}
              className={`segment-btn ${filters.status === opt.value ? 'active' : ''}`}
              onClick={() => handleFilterChange('status', opt.value)}
              role="radio"
              aria-checked={filters.status === opt.value}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div className="segmented-control" role="radiogroup" aria-label="Due date filter">
          {[
            { value: 'upcoming', label: 'Upcoming' },
            { value: 'overdue', label: 'Overdue' },
            { value: 'all', label: 'All' }
          ].map(opt => (
            <button
              key={opt.value}
              className={`segment-btn ${filters.due === opt.value ? 'active' : ''}`}
              onClick={() => handleFilterChange('due', opt.value)}
              role="radio"
              aria-checked={filters.due === opt.value}
            >
              {opt.label}
            </button>
          ))}
        </div>
        {!courseEndeavorId && (
          <select
            id="course-filter"
            className="filter-dropdown compact"
            aria-label="Course"
            value={filters.endeavor}
            onChange={(e) => handleFilterChange('endeavor', e.target.value)}
          >
            <option value="all">All Courses</option>
            {endeavors.map((endeavor) => (
              <option
                key={endeavor.endeavorId || endeavor.id}
                value={(endeavor.endeavorId || endeavor.id).toString()}
              >
                {endeavor.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Progress Summary */}
      {!loading && !error && assignments.length > 0 && (
        <div className="progress-summary-bar">
          <div className="progress-track">
            <div
              className="progress-fill assignments-progress"
              style={{ width: `${Math.round((completedCount / assignments.length) * 100)}%` }}
            />
          </div>
          <span className="progress-text">
            {completedCount} of {assignments.length} completed ({Math.round((completedCount / assignments.length) * 100)}%)
          </span>
        </div>
      )}

      <div className="card-content assignments-content">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading assignments...</p>
          </div>
        ) : error ? (
          <div className="error-state">
            <div className="error-icon">⚠️</div>
            <p>Failed to load assignments: {error.message || error}</p>
            <button type="button" className="btn btn-secondary" onClick={handleRefresh}>🔄 Try Again</button>
          </div>
        ) : filteredAssignments.length === 0 ? (
          <div className="empty-state celebration-empty">
            <div className="mascot-container">
              <div className="mascot">🎉</div>
              <div className="mascot-speech">
                <div className="speech-bubble">
                  You're all caught up!
                </div>
              </div>
            </div>
            <div className="celebration-content">
              <h3>Add Assignments to Start Tracking Your Progress!</h3>
              <p>
                {endeavors.length === 0 ? 'No endeavors found' :
                 filters.status === 'incomplete' ? 'No incomplete assignments right now. Time to relax or get ahead on your studies.' :
                 filters.status === 'complete' ? 'No completed assignments to show' :
                 'No assignments found matching your filters'}
              </p>
            </div>
          </div>
        ) : (
          <div className="assignments-list">
            {filteredAssignments.map((assignment, index) => {
              const isCompleted = Boolean(
                assignment.completed ||
                assignment.status === 'Completed' ||
                assignment.submissionStatus === 'Completed'
              );
              const id = assignment.assignmentId || assignment.id;

             // Derive course/endeavor name for the 2nd line
             const courseLine =
               assignment.endeavorName ??
               assignment.courseName ??
               assignment.course?.name ??
               assignment.endeavor?.name ??
               assignment.endeavorTitle ??
               assignment.endeavor?.title ??
               assignment.course?.title ??
               null;

              return (
                <div 
                  key={`${id || index}-${assignment.endeavorId || 'no-endeavor'}-${index}`} 
                  className={`assignment-item compact ${isCompleted ? 'is-completed' : ''}`}
                  style={{ animationDelay: `${index * 0.05}s` }}
                  onClick={() => handleAssignmentClick(assignment)}
                >
                  <div className="assignment-content">
                    {/* Title on top, full width, single line */}
                    <div className="assignment-header-title">
                      <h4 className="assignment-title single-line">
                        {assignment.name || assignment.title}
                      </h4>
                     {courseLine && (
                       <div className="assignment-course single-line" title={courseLine}>
                         {courseLine}
                       </div>
                     )}
                    </div>

                    {/* Remaining content row */}
                    <div className="assignment-row">
                      <span className="due-date-text">
                        {formatDueDateForAssignment(assignment)}
                      </span>
                      {/* Completion toggle button (info intent theme) */}
                      <button
                        type="button"
                        className={`completion-toggle ${isCompleted ? 'is-complete' : 'is-incomplete'}`}
                        disabled={completingId === id}
                        onClick={(e)=>{
                          e.stopPropagation();
                          handleToggleCompletion(assignment, !isCompleted);
                        }}
                        aria-pressed={isCompleted}
                        aria-label={isCompleted ? 'Mark assignment as incomplete' : 'Mark assignment as complete'}
                        title={isCompleted ? 'Mark As Incomplete' : 'Mark As Complete'}
                      >
                        {completingId === id ? (
                          <span className="toggle-loading"></span>
                        ) : isCompleted ? (
                          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="10" fill="var(--status-success, #22c55e)"/>
                            <path d="M8 12l3 3 5-5" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        ) : (
                          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="10" stroke="var(--border-default)" strokeWidth="1.5" fill="none"/>
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {filteredAssignments.length > 3 && (
          <div className="list-footer">
            {filteredAssignments.length} total assignments
          </div>
        )}
      </div>

      <AssignmentCompletionModal
        open={completionModal.open}
        assignment={completionModal.assignment}
        action={completionModal.action}
        loading={completionModal.loading}
        onConfirm={confirmCompletionChange}
        onClose={() => {
          if (completionModal.loading) return;
          setCompletionModal({
            open: false,
            action: null,
            assignment: null,
            targetCompleted: null,
            loading: false
          });
        }}
      />
    </div>
  );
}

export default UpcomingAssignments;